/**
 * HoldingsTable - Enhanced sortable, searchable table of portfolio positions.
 *
 * Features:
 * - Inline sparkline chart and bull/bear stance gauge per row
 * - Expandable rows: click to show bull/bear thesis, news, fundamentals
 * - On-demand fetch of Yahoo Finance news + FMP key metrics when expanded
 * - Deep Dive button in expanded panel
 * - Responsive: hides columns progressively on smaller screens
 */

import { useState, useMemo, useCallback } from 'react';
import {
  ArrowUpDown,
  TrendingUp,
  Search,
  ChevronDown,
  ChevronRight,
  Zap,
  AlertTriangle,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';
import { InfoTooltip } from '@/components/ui/Tooltip';
import { TruncatedText } from '@/components/ui/TruncatedText';
import { TickerLogo } from '@/components/ui/TickerLogo';
import { MiniSparkline } from './MiniSparkline';
import { StanceGauge } from './StanceGauge';
import type { PositionWithScan } from '@/types/portfolio';

type SortField = 'symbol' | 'current_value' | 'total_gain_loss' | 'total_gain_loss_pct' | 'quantity';
type SortDir = 'asc' | 'desc';

interface HoldingsTableProps {
  positions: PositionWithScan[];
  sparklines: Record<string, number[]>;
  onDeepDive: (ticker: string) => void;
}

interface TickerDetail {
  symbol: string;
  news: Array<{
    title: string;
    publisher: string;
    link: string;
    published_date: string;
  }>;
  profile: {
    company_name: string;
    sector: string;
    industry: string;
    description: string;
    market_cap: number;
    employees: number;
  } | null;
  key_metrics: {
    pe_ratio: number;
    pb_ratio: number;
    ps_ratio: number;
    ev_to_ebitda: number;
    debt_to_equity: number;
    current_ratio: number;
    roe: number;
    roa: number;
    dividend_yield: number;
  } | null;
  analyst_data: {
    recommendations?: {
      strong_buy: number;
      buy: number;
      hold: number;
      sell: number;
      strong_sell: number;
      total: number;
      consensus: string;
    };
    price_targets?: {
      current: number | null;
      mean: number | null;
      high: number | null;
      low: number | null;
      median: number | null;
      num_analysts: number;
      upside_pct: number | null;
    };
  } | null;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatLargeCurrency(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return formatCurrency(value);
}

function formatMetric(value: number | null | undefined, suffix = ''): string {
  if (value == null || isNaN(value) || value === 0) return '—';
  return `${value.toFixed(2)}${suffix}`;
}

export function HoldingsTable({ positions, sparklines, onDeepDive }: HoldingsTableProps) {
  const [sortField, setSortField] = useState<SortField>('current_value');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTicker, setExpandedTicker] = useState<string | null>(null);
  const [tickerDetails, setTickerDetails] = useState<Record<string, TickerDetail>>({});
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);

  const hasScanResults = positions.some((p) => p.scan);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return positions;
    const q = searchQuery.toLowerCase();
    return positions.filter(
      (p) =>
        p.symbol.toLowerCase().includes(q) ||
        (p.name && p.name.toLowerCase().includes(q))
    );
  }, [positions, searchQuery]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [filtered, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleRowClick = useCallback(
    async (symbol: string) => {
      if (expandedTicker === symbol) {
        setExpandedTicker(null);
        return;
      }

      setExpandedTicker(symbol);

      // Fetch detail data if not already cached
      if (!tickerDetails[symbol]) {
        setLoadingDetail(symbol);
        try {
          const token = localStorage.getItem('access_token');
          const res = await fetch(
            `${API_BASE_URL}/api/v1/portfolio/ticker-detail/${symbol}`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
          );
          if (res.ok) {
            const data = await res.json();
            setTickerDetails((prev) => ({ ...prev, [symbol]: data }));
          }
        } catch {
          // Silent fail — detail section will just not show API data
        } finally {
          setLoadingDetail(null);
        }
      }
    },
    [expandedTicker, tickerDetails]
  );

  const fullColSpan = 10;

  const SortHeader = ({
    field,
    children,
    className,
    tooltip,
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
    tooltip?: string;
  }) => (
    <th
      onClick={(e) => {
        e.stopPropagation();
        toggleSort(field);
      }}
      className={cn(
        'px-3 sm:px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider cursor-pointer hover:text-text-primary transition-colors select-none',
        className
      )}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown
          className={cn('w-3 h-3', sortField === field ? 'text-primary' : 'text-text-muted/40')}
        />
        {tooltip && <InfoTooltip content={tooltip} position="top" />}
      </div>
    </th>
  );

  return (
    <div>
      {/* Search Bar */}
      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter holdings..."
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-surface-light border border-border text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
          />
        </div>
        <span className="text-xs text-text-muted">
          {filtered.length === positions.length
            ? `${positions.length} positions`
            : `${filtered.length} of ${positions.length} positions`}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[700px]">
          <thead className="bg-surface-light">
            <tr>
              <th className="w-6 px-1 py-3" /> {/* Expand icon */}
              <SortHeader field="symbol">Symbol</SortHeader>
              <th className="px-2 py-3 text-xs font-medium text-text-muted uppercase tracking-wider hidden md:table-cell">
                30D
              </th>
              <SortHeader
                field="quantity"
                className="text-right hidden sm:table-cell"
                tooltip="Number of shares or units held."
              >
                Qty
              </SortHeader>
              <SortHeader
                field="current_value"
                className="text-right"
                tooltip="Current market value (Qty x Price)."
              >
                Value
              </SortHeader>
              <SortHeader
                field="total_gain_loss"
                className="text-right hidden md:table-cell"
                tooltip="Total dollar gain or loss since purchase."
              >
                Gain/Loss
              </SortHeader>
              <SortHeader
                field="total_gain_loss_pct"
                className="text-right"
                tooltip="Total percentage change from your cost basis."
              >
                %
              </SortHeader>
              {hasScanResults && (
                <th className="px-2 py-3 text-xs font-medium text-text-muted uppercase tracking-wider hidden sm:table-cell">
                  Lean
                </th>
              )}
              <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider whitespace-nowrap">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((pos) => {
              const isPositive = pos.total_gain_loss >= 0;
              const isExpanded = expandedTicker === pos.symbol;
              const sparklineData = sparklines[pos.symbol];
              const detail = tickerDetails[pos.symbol];
              const isLoadingThis = loadingDetail === pos.symbol;

              return (
                <RowGroup key={`${pos.symbol}-${pos.account}`}>
                  {/* Main row */}
                  <tr
                    onClick={() => handleRowClick(pos.symbol)}
                    className={cn(
                      'hover:bg-surface-light/50 transition-colors cursor-pointer',
                      isExpanded && 'bg-surface-light/30'
                    )}
                  >
                    <td className="w-6 px-1 py-3 text-text-muted">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <div className="flex items-center gap-2">
                        <TickerLogo symbol={pos.symbol} name={pos.name} size="xs" />
                        <div>
                          <span className="font-mono font-semibold text-text-primary">
                            {pos.symbol}
                          </span>
                          {pos.name && (
                            <TruncatedText text={pos.name} className="text-xs text-text-muted max-w-[120px] sm:max-w-[200px]" />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 hidden md:table-cell">
                      {sparklineData && sparklineData.length > 1 && (
                        <MiniSparkline data={sparklineData} width={60} height={20} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-text-secondary hidden sm:table-cell">
                      {pos.quantity.toLocaleString(undefined, { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-right text-sm font-medium text-text-primary">
                      {formatCurrency(pos.current_value)}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-right text-sm font-medium hidden md:table-cell',
                        isPositive ? 'text-green-400' : 'text-red-400'
                      )}
                    >
                      {isPositive ? '+' : ''}
                      {formatCurrency(pos.total_gain_loss)}
                    </td>
                    <td
                      className={cn(
                        'px-3 sm:px-4 py-3 text-right text-sm',
                        isPositive ? 'text-green-400' : 'text-red-400'
                      )}
                    >
                      {isPositive ? '+' : ''}
                      {pos.total_gain_loss_pct.toFixed(2)}%
                    </td>
                    {hasScanResults && (
                      <td className="px-2 py-3 hidden sm:table-cell" style={{ minWidth: 120 }}>
                        {pos.scan && (
                          <StanceGauge
                            bullIntensity={pos.scan.bull_intensity}
                            bearRisk={pos.scan.bear_risk}
                            stance={pos.scan.stance}
                            size="sm"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-3 sm:px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeepDive(pos.symbol);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 hover:scale-105 active:scale-95 transition-all duration-200"
                      >
                        <TrendingUp className="w-3 h-3" />
                        Analyze
                      </button>
                    </td>
                  </tr>

                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={fullColSpan} className="px-4 py-4 bg-surface/80 border-t border-border/50">
                        <ExpandedRowDetail
                          position={pos}
                          detail={detail}
                          isLoading={isLoadingThis}
                          onDeepDive={onDeepDive}
                        />
                      </td>
                    </tr>
                  )}
                </RowGroup>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Fragment wrapper for grouping main row + expanded row */
function RowGroup({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

// ─────────────────────────────────────────────────────────────
// Expanded Row Detail
// ─────────────────────────────────────────────────────────────

function ExpandedRowDetail({
  position,
  detail,
  isLoading,
  onDeepDive,
}: {
  position: PositionWithScan;
  detail?: TickerDetail;
  isLoading: boolean;
  onDeepDive: (ticker: string) => void;
}) {
  const { scan } = position;

  return (
    <div className="space-y-4">
      {/* Position summary */}
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Shares</p>
          <p className="text-sm text-text-primary font-semibold tabular-nums">
            {position.quantity.toLocaleString(undefined, { maximumFractionDigits: 3 })}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Avg Cost</p>
          <p className="text-sm text-text-primary font-semibold tabular-nums">
            {formatCurrency(position.cost_basis)}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Price</p>
          <p className="text-sm text-text-primary font-semibold tabular-nums">
            {formatCurrency(position.current_price)}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Value</p>
          <p className="text-sm text-text-primary font-semibold tabular-nums">
            {formatCurrency(position.current_value)}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-wider">P&amp;L</p>
          <p className={cn(
            'text-sm font-semibold tabular-nums',
            position.total_gain_loss >= 0 ? 'text-green-400' : 'text-red-400'
          )}>
            {position.total_gain_loss >= 0 ? '+' : ''}
            {formatCurrency(position.total_gain_loss)} ({position.total_gain_loss >= 0 ? '+' : ''}{position.total_gain_loss_pct.toFixed(2)}%)
          </p>
        </div>
        {detail?.profile && (
          <div className="ml-auto">
            <p className="text-[10px] text-text-muted uppercase tracking-wider">Sector</p>
            <p className="text-xs text-text-secondary">
              {detail.profile.sector} &middot; {detail.profile.industry}
            </p>
          </div>
        )}
      </div>

      {/* Bull / Bear Thesis */}
      {scan && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg bg-green-500/5 border border-green-500/10 p-3 space-y-1.5">
            <span className="text-xs font-semibold text-green-400 uppercase tracking-wider">
              Bull Case
            </span>
            <p className="text-sm text-text-secondary leading-relaxed">{scan.bull_thesis}</p>
          </div>
          <div className="rounded-lg bg-red-500/5 border border-red-500/10 p-3 space-y-1.5">
            <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">
              Bear Case
            </span>
            <p className="text-sm text-text-secondary leading-relaxed">{scan.bear_thesis}</p>
          </div>
        </div>
      )}

      {/* Catalysts & Risks chips */}
      {scan && (scan.catalysts.length > 0 || scan.risks.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {scan.catalysts.map((c, i) => (
            <span
              key={`c-${i}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 text-[10px]"
            >
              <Zap className="w-2.5 h-2.5" />
              {c}
            </span>
          ))}
          {scan.risks.map((r, i) => (
            <span
              key={`r-${i}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 text-[10px]"
            >
              <AlertTriangle className="w-2.5 h-2.5" />
              {r}
            </span>
          ))}
        </div>
      )}

      {/* Loading state for API data */}
      {isLoading && (
        <div className="flex items-center gap-2 text-text-muted text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading news &amp; fundamentals...
        </div>
      )}

      {/* News + Key Metrics + Analyst Data (loaded on demand) */}
      {detail && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* News section */}
          <div>
            <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
              Recent News
            </h4>
            {detail.news.length > 0 ? (
              <div className="space-y-2">
                {detail.news.map((n, i) => (
                  <a
                    key={i}
                    href={n.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block group"
                  >
                    <p className="text-sm text-text-secondary group-hover:text-text-primary transition-colors leading-snug">
                      {n.title}
                      <ExternalLink className="w-3 h-3 inline ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                    <p className="text-[10px] text-text-muted mt-0.5">
                      {n.publisher}
                      {n.published_date && ` \u00B7 ${n.published_date}`}
                    </p>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-xs text-text-muted">No recent news available</p>
            )}
          </div>

          {/* Key Metrics section */}
          <div>
            <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
              Key Metrics
            </h4>
            {detail.key_metrics ? (
              <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                <MetricItem label="P/E" value={formatMetric(detail.key_metrics.pe_ratio)} />
                <MetricItem label="P/B" value={formatMetric(detail.key_metrics.pb_ratio)} />
                <MetricItem label="P/S" value={formatMetric(detail.key_metrics.ps_ratio)} />
                <MetricItem label="EV/EBITDA" value={formatMetric(detail.key_metrics.ev_to_ebitda)} />
                <MetricItem label="D/E" value={formatMetric(detail.key_metrics.debt_to_equity)} />
                <MetricItem label="Current" value={formatMetric(detail.key_metrics.current_ratio)} />
                <MetricItem label="ROE" value={formatMetric(detail.key_metrics.roe, '%')} />
                <MetricItem label="ROA" value={formatMetric(detail.key_metrics.roa, '%')} />
                <MetricItem
                  label="Div Yield"
                  value={formatMetric(detail.key_metrics.dividend_yield, '%')}
                />
              </div>
            ) : (
              <p className="text-xs text-text-muted">Metrics unavailable</p>
            )}

            {/* Company profile snippet */}
            {detail.profile && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <p className="text-xs text-text-muted leading-relaxed">
                  {detail.profile.description}
                </p>
                <div className="flex gap-3 mt-1.5 text-[10px] text-text-muted/60">
                  {detail.profile.market_cap > 0 && (
                    <span>Mkt Cap: {formatLargeCurrency(detail.profile.market_cap)}</span>
                  )}
                  {detail.profile.employees > 0 && (
                    <span>{detail.profile.employees.toLocaleString()} employees</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Analyst Consensus + Price Targets */}
        {detail.analyst_data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Analyst Recommendations */}
            {detail.analyst_data.recommendations && detail.analyst_data.recommendations.total > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
                  Analyst Consensus
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-semibold',
                      detail.analyst_data.recommendations.consensus.includes('Buy') && 'bg-green-500/15 text-green-400',
                      detail.analyst_data.recommendations.consensus === 'Hold' && 'bg-yellow-500/15 text-yellow-400',
                      detail.analyst_data.recommendations.consensus.includes('Sell') && 'bg-red-500/15 text-red-400',
                    )}>
                      {detail.analyst_data.recommendations.consensus}
                    </span>
                    <span className="text-xs text-text-muted">
                      {detail.analyst_data.recommendations.total} analysts
                    </span>
                  </div>
                  {/* Stacked bar */}
                  <div className="flex h-2 rounded-full overflow-hidden bg-surface-light">
                    {detail.analyst_data.recommendations.strong_buy > 0 && (
                      <div
                        className="bg-green-500"
                        style={{ width: `${(detail.analyst_data.recommendations.strong_buy / detail.analyst_data.recommendations.total) * 100}%` }}
                        title={`Strong Buy: ${detail.analyst_data.recommendations.strong_buy}`}
                      />
                    )}
                    {detail.analyst_data.recommendations.buy > 0 && (
                      <div
                        className="bg-green-400"
                        style={{ width: `${(detail.analyst_data.recommendations.buy / detail.analyst_data.recommendations.total) * 100}%` }}
                        title={`Buy: ${detail.analyst_data.recommendations.buy}`}
                      />
                    )}
                    {detail.analyst_data.recommendations.hold > 0 && (
                      <div
                        className="bg-yellow-400"
                        style={{ width: `${(detail.analyst_data.recommendations.hold / detail.analyst_data.recommendations.total) * 100}%` }}
                        title={`Hold: ${detail.analyst_data.recommendations.hold}`}
                      />
                    )}
                    {detail.analyst_data.recommendations.sell > 0 && (
                      <div
                        className="bg-red-400"
                        style={{ width: `${(detail.analyst_data.recommendations.sell / detail.analyst_data.recommendations.total) * 100}%` }}
                        title={`Sell: ${detail.analyst_data.recommendations.sell}`}
                      />
                    )}
                    {detail.analyst_data.recommendations.strong_sell > 0 && (
                      <div
                        className="bg-red-500"
                        style={{ width: `${(detail.analyst_data.recommendations.strong_sell / detail.analyst_data.recommendations.total) * 100}%` }}
                        title={`Strong Sell: ${detail.analyst_data.recommendations.strong_sell}`}
                      />
                    )}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 text-[10px] text-text-muted">
                    {detail.analyst_data.recommendations.strong_buy > 0 && (
                      <span><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-0.5" /> Strong Buy {detail.analyst_data.recommendations.strong_buy}</span>
                    )}
                    {detail.analyst_data.recommendations.buy > 0 && (
                      <span><span className="inline-block w-2 h-2 rounded-full bg-green-400 mr-0.5" /> Buy {detail.analyst_data.recommendations.buy}</span>
                    )}
                    {detail.analyst_data.recommendations.hold > 0 && (
                      <span><span className="inline-block w-2 h-2 rounded-full bg-yellow-400 mr-0.5" /> Hold {detail.analyst_data.recommendations.hold}</span>
                    )}
                    {detail.analyst_data.recommendations.sell > 0 && (
                      <span><span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-0.5" /> Sell {detail.analyst_data.recommendations.sell}</span>
                    )}
                    {detail.analyst_data.recommendations.strong_sell > 0 && (
                      <span><span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-0.5" /> Strong Sell {detail.analyst_data.recommendations.strong_sell}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Price Targets */}
            {detail.analyst_data.price_targets && (
              <div>
                <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
                  Price Targets
                </h4>
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  <MetricItem label="Low" value={detail.analyst_data.price_targets.low ? formatCurrency(detail.analyst_data.price_targets.low) : '—'} />
                  <MetricItem label="Mean" value={detail.analyst_data.price_targets.mean ? formatCurrency(detail.analyst_data.price_targets.mean) : '—'} />
                  <MetricItem label="High" value={detail.analyst_data.price_targets.high ? formatCurrency(detail.analyst_data.price_targets.high) : '—'} />
                </div>
                {detail.analyst_data.price_targets.upside_pct != null && (
                  <p className={cn(
                    'text-xs font-medium mt-2',
                    detail.analyst_data.price_targets.upside_pct >= 0 ? 'text-green-400' : 'text-red-400'
                  )}>
                    {detail.analyst_data.price_targets.upside_pct >= 0 ? '+' : ''}
                    {detail.analyst_data.price_targets.upside_pct.toFixed(1)}% to mean target
                    {detail.analyst_data.price_targets.num_analysts > 0 && (
                      <span className="text-text-muted font-normal"> ({detail.analyst_data.price_targets.num_analysts} analysts)</span>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        </>
      )}

      {/* Deep Dive button */}
      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDeepDive(position.symbol);
          }}
          className={cn(
            'flex items-center gap-1.5',
            'px-4 py-2 rounded-lg text-xs font-medium',
            'bg-primary/10 text-primary hover:bg-primary/20 transition-colors'
          )}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          Deep Dive Analysis
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function MetricItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-text-muted">{label}</p>
      <p className="text-sm text-text-primary font-medium tabular-nums">{value}</p>
    </div>
  );
}
